import { useState } from 'react';
import { Plus, Trash2, Check, Pencil, X } from 'lucide-react';
import { useMachines } from '../../contexts/MachineContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { Machine, LaserSource, LaserType, LaserSoftware, ControllerType } from '@jigflow/shared';

const MANUFACTURERS: Record<string, string[]> = {
  'xTool': ['F1', 'F1 Lite', 'F1 Ultra', 'F2', 'F2 Ultra', 'Laserbox', 'Laserbox Pro', 'P2', 'P2S', 'P3', 'D1 5W', 'D1 10W', 'D1 Pro 10W', 'D1 Pro 20W', 'D1 Pro 40W', 'M1', 'M1 Ultra', 'S1 10W', 'S1 20W', 'S1 40W'],
  'OmTech': ['Polar', 'K40', 'AF2028', 'AF2435'],
  'Thunder Laser': ['Bolt', 'Nova 24', 'Nova 35', 'Nova 51', 'Odin 22', 'Odin 32'],
  'Glowforge': ['Aura', 'Spark', 'Plus', 'Pro'],
  'Epilog': ['Fusion Edge 12', 'Fusion Edge 24', 'Fusion Pro 32', 'Fusion Pro 48'],
};

interface MachineSpec { width: number; height: number; maxSpeed: number; lasers: LaserSource[] }

const MACHINE_SPECS: Record<string, MachineSpec> = {
  'xTool F1': { width: 115, height: 115, maxSpeed: 4000, lasers: [{ type: 'Diode', power: 10 }, { type: 'Infrared', power: 2 }] },
  'xTool F1 Lite': { width: 115, height: 115, maxSpeed: 4000, lasers: [{ type: 'Diode', power: 10 }] },
  'xTool F1 Ultra': { width: 220, height: 220, maxSpeed: 10000, lasers: [{ type: 'Fiber', power: 20 }, { type: 'Diode', power: 20 }] },
  'xTool F2': { width: 115, height: 115, maxSpeed: 6000, lasers: [{ type: 'Diode', power: 15 }, { type: 'Infrared', power: 5 }] },
  'xTool F2 Ultra': { width: 220, height: 220, maxSpeed: 15000, lasers: [{ type: 'Fiber', power: 60 }, { type: 'Diode', power: 40 }] },
  'xTool Laserbox': { width: 500, height: 300, maxSpeed: 350, lasers: [{ type: 'CO2', power: 40 }] },
  'xTool Laserbox Pro': { width: 500, height: 300, maxSpeed: 600, lasers: [{ type: 'CO2', power: 40 }] },
  'xTool P2': { width: 600, height: 305, maxSpeed: 600, lasers: [{ type: 'CO2', power: 55 }] },
  'xTool P2S': { width: 600, height: 307, maxSpeed: 600, lasers: [{ type: 'CO2', power: 55 }] },
  'xTool P3': { width: 915, height: 458, maxSpeed: 1200, lasers: [{ type: 'CO2', power: 80 }] },
  'xTool D1 5W': { width: 432, height: 406, maxSpeed: 160, lasers: [{ type: 'Diode', power: 5 }] },
  'xTool D1 10W': { width: 432, height: 406, maxSpeed: 180, lasers: [{ type: 'Diode', power: 10 }] },
  'xTool D1 Pro 10W': { width: 430, height: 400, maxSpeed: 400, lasers: [{ type: 'Diode', power: 10 }] },
  'xTool D1 Pro 20W': { width: 430, height: 400, maxSpeed: 400, lasers: [{ type: 'Diode', power: 20 }] },
  'xTool D1 Pro 40W': { width: 430, height: 390, maxSpeed: 400, lasers: [{ type: 'Diode', power: 40 }] },
  'xTool M1': { width: 385, height: 300, maxSpeed: 250, lasers: [{ type: 'Diode', power: 10 }] },
  'xTool M1 Ultra': { width: 300, height: 300, maxSpeed: 400, lasers: [{ type: 'Diode', power: 20 }] },
  'xTool S1 10W': { width: 498, height: 319, maxSpeed: 400, lasers: [{ type: 'Diode', power: 10 }] },
  'xTool S1 20W': { width: 498, height: 330, maxSpeed: 600, lasers: [{ type: 'Diode', power: 20 }] },
  'xTool S1 40W': { width: 498, height: 319, maxSpeed: 600, lasers: [{ type: 'Diode', power: 40 }] },
  'OmTech Polar': { width: 500, height: 300, maxSpeed: 600, lasers: [{ type: 'CO2', power: 50 }] },
  'OmTech K40': { width: 300, height: 200, maxSpeed: 300, lasers: [{ type: 'CO2', power: 40 }] },
  'OmTech AF2028': { width: 500, height: 700, maxSpeed: 600, lasers: [{ type: 'CO2', power: 80 }] },
  'OmTech AF2435': { width: 600, height: 900, maxSpeed: 600, lasers: [{ type: 'CO2', power: 80 }] },
  'Thunder Laser Bolt': { width: 508, height: 305, maxSpeed: 1000, lasers: [{ type: 'CO2', power: 30 }] },
  'Thunder Laser Nova 24': { width: 600, height: 400, maxSpeed: 1000, lasers: [{ type: 'CO2', power: 80 }] },
  'Thunder Laser Nova 35': { width: 900, height: 600, maxSpeed: 1000, lasers: [{ type: 'CO2', power: 100 }] },
  'Thunder Laser Nova 51': { width: 1300, height: 900, maxSpeed: 1000, lasers: [{ type: 'CO2', power: 130 }] },
  'Thunder Laser Odin 22': { width: 559, height: 381, maxSpeed: 2000, lasers: [{ type: 'CO2', power: 30 }] },
  'Thunder Laser Odin 32': { width: 813, height: 508, maxSpeed: 2000, lasers: [{ type: 'CO2', power: 55 }] },
  'Glowforge Aura': { width: 305, height: 305, maxSpeed: 150, lasers: [{ type: 'Diode', power: 6 }] },
  'Glowforge Spark': { width: 216, height: 279, maxSpeed: 150, lasers: [{ type: 'Diode', power: 6 }] },
  'Glowforge Plus': { width: 495, height: 279, maxSpeed: 250, lasers: [{ type: 'CO2', power: 40 }] },
  'Glowforge Pro': { width: 495, height: 279, maxSpeed: 300, lasers: [{ type: 'CO2', power: 45 }] },
  'Epilog Fusion Edge 12': { width: 610, height: 305, maxSpeed: 3050, lasers: [{ type: 'CO2', power: 50 }] },
  'Epilog Fusion Edge 24': { width: 610, height: 610, maxSpeed: 3050, lasers: [{ type: 'CO2', power: 75 }] },
  'Epilog Fusion Pro 32': { width: 813, height: 508, maxSpeed: 4200, lasers: [{ type: 'CO2', power: 80 }] },
  'Epilog Fusion Pro 48': { width: 1219, height: 914, maxSpeed: 4200, lasers: [{ type: 'CO2', power: 120 }] },
};

export default function MachinesTab() {
  const { machines, loading, addMachine, updateMachine, deleteMachine, setActiveMachine } = useMachines();
  const { isWithinLimit } = useWorkspace();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [customName, setCustomName] = useState('');
  const [width, setWidth] = useState(400);
  const [height, setHeight] = useState(400);
  const [maxSpeed, setMaxSpeed] = useState(600);
  const [lasers, setLasers] = useState<LaserSource[]>([{ type: 'Diode' as LaserType, power: 10 }]);
  const [software, setSoftware] = useState<LaserSoftware>('xtool');
  const [controllerType, setControllerType] = useState<ControllerType>('grbl');

  const canAddMachine = isWithinLimit('machines', machines.length);

  const resetForm = () => {
    setSelectedManufacturer('');
    setSelectedModel('');
    setCustomName('');
    setWidth(400);
    setHeight(400);
    setMaxSpeed(600);
    setLasers([{ type: 'Diode' as LaserType, power: 10 }]);
    setSoftware('xtool');
    setControllerType('grbl');
    setEditingId(null);
  };

  const applyPreset = (manufacturer: string, model: string) => {
    const key = `${manufacturer} ${model}`;
    const spec = MACHINE_SPECS[key];
    if (spec) {
      setWidth(spec.width);
      setHeight(spec.height);
      setMaxSpeed(spec.maxSpeed);
      setLasers(spec.lasers);
      setCustomName(key);
      // Auto-detect software
      if (manufacturer === 'xTool') {
        setSoftware('xtool');
        setControllerType('grbl');
      } else {
        setSoftware('lightburn');
        setControllerType('dsp');
      }
    }
  };

  const handleSave = async () => {
    const name = customName || (selectedManufacturer && selectedModel ? `${selectedManufacturer} ${selectedModel}` : 'Custom Machine');
    const data: Partial<Machine> = {
      name,
      width,
      height,
      maxSpeed,
      lasers,
      software,
      controllerType,
      active: machines.length === 0,
    };

    try {
      if (editingId) {
        await updateMachine(editingId, data);
      } else {
        await addMachine(data);
      }
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('[Machines] Failed to save:', err);
    }
  };

  const openEdit = (machine: Machine) => {
    setEditingId(machine.id);
    setCustomName(machine.name);
    setWidth(machine.width);
    setHeight(machine.height);
    setMaxSpeed(machine.maxSpeed || 600);
    setLasers(machine.lasers);
    setSoftware(machine.software || 'xtool');
    setControllerType(machine.controllerType || 'grbl');
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this machine?')) return;
    try {
      await deleteMachine(id);
    } catch (err) {
      console.error('[Machines] Failed to delete:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Machines</h3>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          disabled={!canAddMachine}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition"
        >
          <Plus size={16} />
          Add Machine
        </button>
      </div>

      {!canAddMachine && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
          Machine limit reached for your plan. Upgrade to add more.
        </div>
      )}

      {/* Machine List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      ) : machines.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p>No machines configured</p>
          <p className="text-sm mt-1">Add a machine to start generating files</p>
        </div>
      ) : (
        <div className="space-y-3">
          {machines.map(machine => (
            <div
              key={machine.id}
              className={`bg-white dark:bg-gray-800 border rounded-xl p-4 flex items-center gap-4 ${
                machine.active ? 'border-blue-500 ring-1 ring-blue-500/20' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900 dark:text-white">{machine.name}</h4>
                  {machine.active && (
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {machine.width}x{machine.height}mm &middot; {machine.lasers.map(l => `${l.type} ${l.power}W`).join(', ')} &middot; {machine.maxSpeed || 600}mm/s
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!machine.active && (
                  <button
                    onClick={() => setActiveMachine(machine.id)}
                    className="p-2 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg text-green-600 transition"
                    title="Set as active"
                  >
                    <Check size={16} />
                  </button>
                )}
                <button
                  onClick={() => openEdit(machine)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 transition"
                  title="Edit"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => handleDelete(machine.id)}
                  className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500 transition"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingId ? 'Edit Machine' : 'Add Machine'}
              </h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Preset Selection (only for new machines) */}
              {!editingId && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer</label>
                    <select
                      value={selectedManufacturer}
                      onChange={(e) => {
                        setSelectedManufacturer(e.target.value);
                        setSelectedModel('');
                      }}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="">Custom / Other</option>
                      {Object.keys(MANUFACTURERS).map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                  {selectedManufacturer && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                      <select
                        value={selectedModel}
                        onChange={(e) => {
                          setSelectedModel(e.target.value);
                          if (e.target.value) applyPreset(selectedManufacturer, e.target.value);
                        }}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="">Select model...</option>
                        {MANUFACTURERS[selectedManufacturer]?.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="e.g. My xTool F1 Ultra"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Width (mm)</label>
                  <input type="number" value={width} onChange={(e) => setWidth(Number(e.target.value))} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Height (mm)</label>
                  <input type="number" value={height} onChange={(e) => setHeight(Number(e.target.value))} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Speed (mm/s)</label>
                  <input type="number" value={maxSpeed} onChange={(e) => setMaxSpeed(Number(e.target.value))} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" />
                </div>
              </div>

              {/* Laser Sources */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Laser Sources</label>
                {lasers.map((laser, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <select
                      value={laser.type}
                      onChange={(e) => {
                        const updated = [...lasers];
                        updated[idx] = { ...updated[idx], type: e.target.value as LaserType };
                        setLasers(updated);
                      }}
                      className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="Diode">Diode</option>
                      <option value="CO2">CO2</option>
                      <option value="Fiber">Fiber</option>
                      <option value="Infrared">Infrared</option>
                    </select>
                    <input
                      type="number"
                      value={laser.power}
                      onChange={(e) => {
                        const updated = [...lasers];
                        updated[idx] = { ...updated[idx], power: Number(e.target.value) };
                        setLasers(updated);
                      }}
                      className="w-20 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                      placeholder="W"
                    />
                    {lasers.length > 1 && (
                      <button onClick={() => setLasers(lasers.filter((_, i) => i !== idx))} className="p-2 text-red-400 hover:text-red-600">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {lasers.length < 3 && (
                  <button
                    onClick={() => setLasers([...lasers, { type: 'Diode' as LaserType, power: 10 }])}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Add laser source
                  </button>
                )}
              </div>

              {/* Software */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Software</label>
                  <select value={software} onChange={(e) => setSoftware(e.target.value as LaserSoftware)} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm">
                    <option value="xtool">xTool Creative Space</option>
                    <option value="lightburn">LightBurn</option>
                    <option value="generic">Generic</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Controller</label>
                  <select value={controllerType} onChange={(e) => setControllerType(e.target.value as ControllerType)} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm">
                    <option value="grbl">GRBL</option>
                    <option value="dsp">DSP</option>
                    <option value="galvo">Galvo</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition"
              >
                {editingId ? 'Save Changes' : 'Add Machine'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
